import { getDefaultDateSettings, getGlobalField } from "../../_store/helpers";

// This function takes a global field configuration and resolves its filter default value based on the field type and the specified default value. It handles various cases such as boolean, integer, date types, and special string values like "today", "currentmonth", etc.
function resolveFilterDefaultValue(globalField) {

    // Retrieve current fiscal year, month, and period from the default date settings, as they may be needed for resolving certain default values
    const currentFiscalYear = getDefaultDateSettings().currentFiscalYear;
    const currentFiscalMonth = getDefaultDateSettings().currentFiscalMonth;
    const thisFiscalPeriod = getDefaultDateSettings().thisFiscalPeriod;

    const value = globalField.filterDefaultValue;

    // If there is no default value specified, return null to indicate that no filter should be applied for this field
    if (!value) return null;

    // Handle different types of default values based on the global field's type and the nature of the default value
    if (globalField.type === "Boolean" && typeof value === "boolean") {
        return value;
    }

    // If the default value is already an array, a Date object, or a number for an integer field, return it as is since it's already in the expected format for filtering
    if (Array.isArray(value)) {
        return value;
    }

    // If the value is a Date object, return it directly as it's already in the correct format for date filtering
    if (value instanceof Date) {
        return value;
    }

    // If the field is of type Integer and the value is a number, return it directly as it's already in the correct format for integer filtering
    if (globalField.type === "Integer" && typeof value === "number") {
        return value;
    }

    // If the field is of type Integer but the value is an object (e.g., an array or a complex object), return it as is, assuming that the application can handle this format for integer filtering
    if (globalField.type === "Integer" && value instanceof Object) {
        return value;
    }

    // If the field is of type Date but the value is an object (e.g., an array or a complex object), return it as is, assuming that the application can handle this format for date filtering
    if (globalField.type === "Date" && value instanceof Object) {
        return value;
    }

    // If the value is a string, we need to check for special keywords that represent dynamic date values (e.g., "today", "currentmonth") and resolve them to actual date values based on the current date and the default date settings
    if (typeof value === "string") {
        const lower = value.toLowerCase();

        if (lower === "today") return new Date();

        if (lower === "currentday") return [new Date().getDate()];

        if (lower === "currentmonth") return [new Date().getMonth()];

        if (lower === "currentyear") return [new Date().getFullYear()];

        if (lower === "currentfiscalyear" && currentFiscalYear) {
            return currentFiscalYear;
        }

        if (lower === "currentfiscalmonth" && currentFiscalMonth)
            return currentFiscalMonth;

        if (lower === "thisfiscalperiod" && thisFiscalPeriod)
            return thisFiscalPeriod;
    }

    // If none of the above conditions are met, return the original value as is, which may be a static default value that doesn't require any special handling
    return value;
}

// This function takes the filter definition array and the column configuration, identifies date fields, and updates their filter values based on the global field settings. It recursively processes nested filter groups as well.
function syncDateFilters(filterArray, columnConfig) {
    if (!Array.isArray(filterArray)) return filterArray;

    // Create a map of dataField to column config for quick lookup
    columnConfig = columnConfig.map(element => {
        if (element && element.screenField) {
            return {
                ...element,
                ...getGlobalField(element.screenField)
            };
        }
        return element;
    });

    // Filter only date-related columns and create a map for easy access
    const dateColumnMap = new Map(
        columnConfig
            .filter(col => (col && col.screenField) && (
                col.dataType === "date" ||
                col.type === "Date" ||
                col.screenField.toLowerCase().includes("year") || col.screenField.toLowerCase().includes("month") || col.screenField.toLowerCase().includes("date")) && col.screenField.filterDefaultValue
            )
            .map(col => [col.dataField, col])
    );

    // Recursive function to process the filter array
    function process(group) {
        if (!Array.isArray(group)) return group;

        const result = [];
        const handledDateFields = new Set();

        // Iterate through each item in the filter group
        for (let item of group) {

            // Check if the item is a filter condition (array of [field, operator, value])
            if (Array.isArray(item) && item.length === 3) {
                const fieldName = item[0];
                const column = dateColumnMap.get(fieldName);

                // If the column is a date field and we haven't already handled it, resolve the default value
                if (column) {

                    // Avoid processing the same date field multiple times in case of multiple conditions on the same field
                    if (handledDateFields.has(fieldName)) continue;
                    handledDateFields.add(fieldName);
                    const resolvedValue = resolveFilterDefaultValue(column);

                    // If the resolved value is null or undefined, skip adding this filter condition to the result
                    if (!resolvedValue) continue;

                    // Replace the original filter value with the resolved default value
                    result.push([
                        fieldName,
                        column.filterOperator || "=",
                        resolvedValue
                    ]);

                    continue;
                }
            }

            // If the item is an array but not a filter condition, it might be a nested group, so we process it recursively
            if (Array.isArray(item)) {
                const processed = process(item);
                // Only add the processed group to the result if it has valid filter conditions after processing
                if (processed.length) result.push(processed);
            } else {
                // If the item is not an array, it's likely a logical operator (e.g., "and", "or"), so we add it to the result as is
                result.push(item);
            }
        }

        // Return the processed filter group
        return result;
    }

    // Start processing the filter array from the top level
    return process(filterArray);
}

export default {
    countLineBreaks(text) {
        return (text.match(/\n/g) || []).length;
    },
    syncDateFilters,
}